import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Platforms from './components/Platforms';
import Features from './components/Features';
import Pricing from './components/Pricing';
import Devices from './components/Devices';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Admin from './components/Admin';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import RefundPolicy from './components/RefundPolicy';
import SpaceTechBackground from './components/SpaceTechBackground';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4310';

// Futuristic Brand Box Loading Screen
const LoadingScreen = () => (
  <div className="coprep-auth-loading-screen">
    <div className="loading-bolt-stage">
      <div className="loading-box-halo"></div>
      <div className="loading-bolt-box">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      </div>
    </div>
    <div className="loading-brand-label">
      <span className="nav-logo-text" style={{ fontSize: '1.35rem', fontWeight: 800 }}>
        CrackIT <span className="nav-logo-badge">AI</span>
      </span>
    </div>
    <div className="loading-status-bar">
      <div className="loading-status-pill">
        <span className="loading-pulse-dot"></span>
        <span className="loading-status-text">INITIALIZING SECURE SESSION</span>
      </div>
    </div>
  </div>
);

// Route guard that preserves current URL on refresh and waits for session validation
const ProtectedRoute = ({ user, isAuthLoading, requiredRole, children }) => {
  const location = useLocation();

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalError, setGlobalError] = useState('');
  const [globalSuccess, setGlobalSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(() => {
    return !!localStorage.getItem('token');
  });
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Shared interviews history state to preserve data across separate pages
  const [pastInterviews, setPastInterviews] = useState([]);
  const isFetchingAuthRef = useRef(false);

  // Persistent login validation check & credit balance auto-refresh on initial mount or payment callback
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setIsAuthLoading(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');
    
    // Prevent duplicate parallel requests if one is already in flight
    if (isFetchingAuthRef.current) return;
    isFetchingAuthRef.current = true;

    const loadUserData = async () => {
      try {
        // Run user authentication and past interviews fetch in parallel for 2x faster load times
        const [authRes, intRes] = await Promise.all([
          fetch(`${API_URL}/api/auth/current-user`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json()).catch(() => null),
          fetch(`${API_URL}/api/interviews`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(r => r.json()).catch(() => null)
        ]);

        if (authRes && authRes.user) {
          setUser(authRes.user);

          // Fast non-blocking sync with desktop app (with 200ms timeout)
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 200);
            fetch('http://127.0.0.1:4312', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ authToken: token }),
              signal: controller.signal
            }).catch(() => {}).finally(() => clearTimeout(timeoutId));
          } catch (err) {}

          // Map past interviews
          if (intRes && intRes.success && Array.isArray(intRes.interviews)) {
            const mapped = intRes.interviews.map(item => {
              const transcriptions = [];
              if (Array.isArray(item.transcriptions)) {
                item.transcriptions.forEach(t => {
                  const timeStr = t.timestamp ? new Date(t.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '00:00';
                  transcriptions.push({
                    speaker: 'Interviewer',
                    time: timeStr,
                    text: t.content,
                    screenshot: t.screenshot || null,
                    screenshots: t.screenshots || (t.screenshot ? [t.screenshot] : [])
                  });
                  if (t.answer && t.answer.content) {
                    const answerTimeStr = t.answer.timestamp ? new Date(t.answer.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : timeStr;
                    transcriptions.push({
                      speaker: 'Copilot AI',
                      time: answerTimeStr,
                      text: t.answer.content
                    });
                  }
                });
              }
              if (transcriptions.length === 0 && Array.isArray(item.messages)) {
                item.messages.forEach(m => {
                  const speakerName = m.type === 'interviewer' ? 'Interviewer' : 'Copilot AI';
                  const timeStr = m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '00:00';
                  transcriptions.push({
                    speaker: speakerName,
                    time: timeStr,
                    text: m.content
                  });
                });
              }
              const modelMapping = {
                gemini: 'Google Gemini 1.5 Pro',
                claude: 'Anthropic Claude 3.5 Sonnet',
                gpt4: 'OpenAI GPT-4o Omniverse'
              };
              return {
                id: item.sessionId,
                category: item.config?.interviewType || 'technical',
                status: item.status || 'active',
                modelName: modelMapping[item.config?.model] || 'Google Gemini 1.5 Pro',
                timeSpent: item.duration ? `${Math.ceil(item.duration / 60)} min` : '0 min',
                creditsSpent: item.tokensConsumed ? `${Math.round(item.tokensConsumed).toLocaleString()} credits` : '1 credit',
                timestamp: item.startedAt ? new Date(item.startedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : 'Unknown Date',
                resume: item.resumeText || 'No resume uploaded',
                transcriptions: transcriptions
              };
            });
            setPastInterviews(mapped);
          }

          if (paymentStatus === 'success') {
            setGlobalSuccess('DEPOSIT CONFIRMED: Added plan credits to your balance.');
            navigate('/dashboard', { replace: true });
          }
        } else if (authRes && authRes.success === false) {
          localStorage.removeItem('token');
          setUser(null);
          setPastInterviews([]);
        }
      } catch (err) {
        console.error('Session validation error:', err);
      } finally {
        isFetchingAuthRef.current = false;
        setIsAuthLoading(false);
      }
    };

    loadUserData();
  }, [location.search]);

  // Centralized robust logout handler
  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.clear();
    setUser(null);
    setIsAuthLoading(false);
    setPastInterviews([]);
    setIsDropdownOpen(false);
    
    // Clear desktop app token sync if active
    try {
      fetch('http://127.0.0.1:4312', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'no-cors',
        body: JSON.stringify({ authToken: '' })
      }).catch(() => {});
    } catch (err) {}

    setGlobalSuccess('Logged out successfully.');
    navigate('/login', { replace: true });
  };

  // Click outside listener for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss toasts
  useEffect(() => {
    if (globalError) {
      const timer = setTimeout(() => setGlobalError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [globalError]);

  useEffect(() => {
    if (globalSuccess) {
      const timer = setTimeout(() => setGlobalSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [globalSuccess]);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isDashboardRoute = location.pathname.startsWith('/dashboard') || 
                          location.pathname.startsWith('/setup') || 
                          location.pathname.startsWith('/store') || 
                          location.pathname.startsWith('/session');
  const isAuthRoute = location.pathname === '/login';
  const isLegalRoute = location.pathname === '/terms' || 
                       location.pathname === '/privacy' || 
                       location.pathname === '/refund-policy' || 
                       location.pathname === '/refund';
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className={`App ${isDashboardRoute ? 'dashboard-mode' : isAuthRoute ? 'auth-mode' : isLegalRoute ? 'legal-mode' : isAdminRoute ? 'admin-mode' : 'landing-mode'}`}>
      
      {/* Interactive Space Starfield & Cosmic Particles Canvas */}
      <SpaceTechBackground />

      {/* Floating Capsule Glass Navbar */}
      {!isDashboardRoute && !isAuthRoute && !isAdminRoute && (
        <nav className="coprep-floating-nav">
          <div className="coprep-nav-inner">
            
            {/* Brand Logo */}
            <div className="coprep-nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <div className="nav-logo-bolt">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              </div>
              <span className="nav-logo-text">CrackIT <span className="nav-logo-badge">AI</span></span>
            </div>

            {/* Centered Desktop Menu Links */}
            <div className="coprep-nav-links">
              <button className="nav-link-btn" onClick={() => scrollToSection('how-it-works')}>How It Works</button>
              <button className="nav-link-btn" onClick={() => scrollToSection('platforms')}>Platforms</button>
              <button className="nav-link-btn" onClick={() => scrollToSection('features')}>Features</button>
              <button className="nav-link-btn" onClick={() => scrollToSection('pricing')}>Pricing</button>
              <button className="nav-link-btn" onClick={() => scrollToSection('download')}>Download</button>
              <button className="nav-link-btn" onClick={() => scrollToSection('faq')}>FAQ</button>
            </div>

            {/* Right Actions (Auth / Dashboard / Credits) */}
            <div className="coprep-nav-actions">
              {user ? (
                <div className="nav-auth-user-group">
                  {/* Credit balance pill */}
                  <div 
                    className="nav-credits-pill"
                    onClick={() => { navigate('/store'); setIsDropdownOpen(false); }}
                    title="Click to refill credits"
                  >
                    <span className="pill-bolt">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                    </span>
                    <span className="pill-amt">{Math.round(user.credits || 0).toLocaleString()}</span>
                  </div>

                  {/* Profile dropdown */}
                  <div className="nav-profile-wrapper" ref={dropdownRef}>
                    <button className="nav-avatar-btn" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                      <span>{user.email ? user.email.charAt(0).toUpperCase() : 'U'}</span>
                    </button>

                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div 
                          className="nav-dropdown-menu glass-card"
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                        >
                          <div className="dropdown-user-header">
                            <span className="user-label">LOGGED IN AS</span>
                            <span className="user-email-text" title={user.email}>{user.email}</span>
                          </div>
                          <div className="dropdown-divider-line"></div>
                          <button className="dropdown-item-btn" onClick={() => { navigate('/dashboard'); setIsDropdownOpen(false); }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                              <rect width="7" height="9" x="3" y="3" rx="1"/>
                              <rect width="7" height="5" x="14" y="3" rx="1"/>
                              <rect width="7" height="9" x="14" y="12" rx="1"/>
                              <rect width="7" height="5" x="3" y="16" rx="1"/>
                            </svg>
                            Main Dashboard
                          </button>
                          <button className="dropdown-item-btn" onClick={() => { navigate('/dashboard/history'); setIsDropdownOpen(false); }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            Past Sessions
                          </button>
                          <button className="dropdown-item-btn" onClick={() => { navigate('/store'); setIsDropdownOpen(false); }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                              <rect width="20" height="14" x="2" y="5" rx="2"/>
                              <line x1="2" y1="10" x2="22" y2="10"/>
                            </svg>
                            Refill Credits
                          </button>
                          {user.role === 'admin' && (
                            <button className="dropdown-item-btn" onClick={() => { navigate('/admin'); setIsDropdownOpen(false); }} style={{ color: '#ef4444', fontWeight: 700 }}>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '8px' }}>
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                              </svg>
                              Admin Control Panel
                            </button>
                          )}
                          <div className="dropdown-divider-line"></div>
                          <button 
                            className="dropdown-item-btn text-danger" 
                            onClick={handleLogout}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                              <polyline points="16 17 21 12 16 7"/>
                              <line x1="21" x2="9" y1="12" y2="12"/>
                            </svg>
                            Sign Out
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <button 
                  className="coprep-nav-signin-btn"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile Hamburger Toggle Button */}
            <button 
              className="coprep-mobile-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation"
            >
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
              <span className="hamburger-bar"></span>
            </button>

          </div>

          {/* Mobile Dropdown Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div 
                className="mobile-nav-drawer glass-card"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div className="mobile-nav-links-list">
                  <button className="mobile-link-item" onClick={() => scrollToSection('how-it-works')}>How It Works</button>
                  <button className="mobile-link-item" onClick={() => scrollToSection('platforms')}>Platforms</button>
                  <button className="mobile-link-item" onClick={() => scrollToSection('features')}>Features</button>
                  <button className="mobile-link-item" onClick={() => scrollToSection('pricing')}>Pricing</button>
                  <button className="mobile-link-item" onClick={() => scrollToSection('download')}>Download</button>
                  <button className="mobile-link-item" onClick={() => scrollToSection('faq')}>FAQ</button>
                  <div className="mobile-nav-divider"></div>
                  {user ? (
                    <button className="mobile-link-btn" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                      Go to Dashboard →
                    </button>
                  ) : (
                    <button className="mobile-link-btn" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                      Sign In →
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      )}

      {/* Global Error Toast */}
      <AnimatePresence>
        {globalError && (
          <motion.div 
            className="coprep-toast toast-error"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
          >
            <div className="toast-content-box">
              <span className="toast-icon">✕</span>
              <span className="toast-msg">{globalError}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global Success Toast */}
      <AnimatePresence>
        {globalSuccess && (
          <motion.div 
            className="coprep-toast toast-success"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
          >
            <div className="toast-content-box">
              <span className="toast-icon">✓</span>
              <span className="toast-msg">{globalSuccess}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* URL Routes */}
      <Routes>
        
        {/* LANDING PAGE ROUTE */}
        <Route path="/" element={
          <div className="landing-page-content">
            <Hero />
            <HowItWorks />
            <Platforms />
            <Features />
            <Pricing 
              user={user} 
              onUpdateUser={setUser} 
              onError={setGlobalError} 
              onSuccess={setGlobalSuccess} 
            />
            <Devices />
            <FAQ />
            <Footer />
          </div>
        } />

        {/* LOGIN / SIGN UP ROUTE */}
        <Route path="/login" element={
          isAuthLoading ? (
            <LoadingScreen />
          ) : user ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Login 
              onLoginSuccess={(userData) => { setUser(userData); setGlobalSuccess('Session authenticated successfully.'); }} 
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          )
        } />

        {/* DASHBOARD OVERVIEW ROUTE (CoPrep AI Layout) */}
        <Route path="/dashboard" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
            <Dashboard 
              user={user} 
              activeTab="overview" 
              pastInterviews={pastInterviews} 
              setPastInterviews={setPastInterviews}
              onLogout={handleLogout} 
              onUpdateUser={setUser}
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          </ProtectedRoute>
        } />

        {/* DASHBOARD SESSIONS HISTORY ROUTE */}
        <Route path="/dashboard/history" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
            <Dashboard 
              user={user} 
              activeTab="history" 
              pastInterviews={pastInterviews} 
              setPastInterviews={setPastInterviews}
              onLogout={handleLogout} 
              onUpdateUser={setUser}
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          </ProtectedRoute>
        } />

        {/* DASHBOARD SETUP INTERVIEW ROUTE */}
        <Route path="/setup" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
            <Dashboard 
              user={user} 
              activeTab="setup" 
              pastInterviews={pastInterviews} 
              setPastInterviews={setPastInterviews}
              onLogout={handleLogout} 
              onUpdateUser={setUser}
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          </ProtectedRoute>
        } />

        {/* DASHBOARD CREDIT STORE ROUTE */}
        <Route path="/store" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
            <Dashboard 
              user={user} 
              activeTab="store" 
              pastInterviews={pastInterviews} 
              setPastInterviews={setPastInterviews}
              onLogout={handleLogout} 
              onUpdateUser={setUser}
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          </ProtectedRoute>
        } />

        {/* SESSION DETAIL VIEW */}
        <Route path="/session/:id" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading}>
            <Dashboard 
              user={user} 
              activeTab="detail" 
              pastInterviews={pastInterviews} 
              setPastInterviews={setPastInterviews}
              onLogout={handleLogout} 
              onUpdateUser={setUser}
              onError={setGlobalError}
              onSuccess={setGlobalSuccess}
            />
          </ProtectedRoute>
        } />

        {/* ADMIN CONTROL PANEL ROUTE */}
        <Route path="/admin" element={
          <ProtectedRoute user={user} isAuthLoading={isAuthLoading} requiredRole="admin">
            <Admin 
              user={user} 
              onLogout={handleLogout} 
              onError={setGlobalError} 
              onSuccess={setGlobalSuccess} 
            />
          </ProtectedRoute>
        } />

        {/* LEGAL PAGES */}
        <Route path="/terms" element={<><Terms /><Footer /></>} />
        <Route path="/privacy" element={<><Privacy /><Footer /></>} />
        <Route path="/refund-policy" element={<><RefundPolicy /><Footer /></>} />
        <Route path="/refund" element={<Navigate to="/refund-policy" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

    </div>
  );
}

export default App;
